import { useState } from "react";
import { Check, CreditCard, Info } from "lucide-react";
import { useT } from "@/lib/i18n/provider";
import { NO_COST_EMI_MIN_INR } from "@/lib/checkout-pricing";
import type { ProductPageOverride } from "@/lib/productPageContent";
import type { PromoCampaign } from "@/lib/promo/inaugural";
import {
  formatInr,
  getPackOptionViews,
  prepaidEstimate,
  type PackOptionView,
} from "@/lib/promo/effectivePacks";

/**
 * Shared 1/2/3-pack selector used by the product page buy box and the
 * home-page quick-buy sheet.
 *
 * All amounts come from the shared resolver (`@/lib/promo/effectivePacks`),
 * which layers admin-published overrides and any live launch campaign on top
 * of the canonical checkout catalog. This component holds NO prices of its
 * own — that duplication is exactly what let the card, headline and sticky
 * bar disagree.
 */

type PackOption = {
  qty: number;
  badge: string;
  supply: string;
  cadence: string;
  saveLabel?: string;
  discount: number;
};

const PACK_OPTIONS: PackOption[] = [
  { qty: 1, badge: "STARTER", supply: "30-day protocol", cadence: "1 bottle", discount: 0 },
  { qty: 2, badge: "MOST POPULAR", supply: "60-day protocol", cadence: "2 bottles · daily ritual", discount: 0.1 },
  { qty: 3, badge: "BEST VALUE", supply: "90-day protocol", cadence: "3 bottles · full reset", discount: 0.2 },
];

/**
 * Back-compat shape for the Admin product page editor, which seeds its
 * override form from the current defaults. Never campaign-aware: admins edit
 * the NORMAL ladder here, which is also what pricing falls back to after a
 * campaign expires.
 */
export function getTieredPackOptions(
  handle: string,
  override?: ProductPageOverride | null,
): Array<{
  qty: 1 | 2 | 3;
  badge: string;
  tablets: number;
  unit: string;
  dailyUse: string;
  supply: string;
  price: number;
  mrp: number;
  perDay: number;
  perUnitLabel: string;
}> | null {
  const views = getPackOptionViews(handle, override, null);
  if (!views) return null;
  return views.map((v) => ({
    qty: v.qty as 1 | 2 | 3,
    badge: v.badge,
    tablets: parseInt(v.unitsLabel, 10) || 0,
    unit: v.unitsLabel.replace(/^\d+\s*/, ""),
    dailyUse: v.dailyUse,
    supply: v.supply,
    price: v.price,
    mrp: v.compareAt || v.price,
    perDay: Math.round(v.price / (v.qty * 30)),
    perUnitLabel: v.perUnitLabel,
  }));
}

function daysForQty(qty: number): number {
  return qty * 30;
}

export function PackSizeSelector({
  handle,
  price,
  qty,
  onSelect,
  override,
  campaign,
}: {
  handle: string;
  price: number;
  qty: number;
  onSelect: (q: number) => void;
  override?: ProductPageOverride | null;
  /** Live launch campaign for this product, if any. */
  campaign?: PromoCampaign | null;
}) {
  const t = useT();
  const [whyOpen, setWhyOpen] = useState(false);
  const tieredOptions: PackOptionView[] | null = getPackOptionViews(
    handle,
    override,
    campaign ?? null,
  );

  return (
    <div className="mt-7">
      <div className="flex items-end justify-between gap-3">
        <p className="text-[12.5px] font-bold uppercase tracking-[0.16em] text-brand-deep">
          {t("pack.selectTitle")}
        </p>
        <button
          type="button"
          onClick={() => setWhyOpen((o) => !o)}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand underline-offset-4 hover:underline"
        >
          <Info className="h-3 w-3" /> {t("pack.whatYouPayFor")}
        </button>
      </div>

      {tieredOptions ? (
        (() => {
          const perDay = (o: PackOptionView) => o.price / daysForQty(o.qty);
          const bestValueQty = tieredOptions.reduce((a, b) =>
            perDay(b) < perDay(a) ? b : a,
          ).qty;
          const single = tieredOptions.find((o) => o.qty === 1);
          const selected = tieredOptions.find((o) => o.qty === qty);
          const savingVsSingles =
            single && selected && selected.qty > 1
              ? Math.round(single.price * selected.qty - selected.price)
              : 0;
          return (
            <>
              <div
                className="mt-5 grid grid-cols-3 gap-2"
                role="radiogroup"
                aria-label="Choose your pack size"
              >
                {tieredOptions.map((opt) => {
                  const isActive = qty === opt.qty;
                  const isBest = opt.qty === bestValueQty;
                  return (
                    <button
                      key={opt.qty}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      onClick={() => onSelect(opt.qty)}
                      className={`group relative flex flex-col rounded-2xl border px-2.5 pb-3 pt-3.5 text-left transition ${
                        isActive
                          ? "border-brand bg-brand-soft/40 shadow-[0_10px_28px_-16px_color-mix(in_oklab,var(--brand)_55%,transparent)]"
                          : "border-border bg-card hover:border-brand/40"
                      }`}
                    >
                      {isBest && (
                        <span className="absolute -top-2.5 left-1/2 max-w-[115%] -translate-x-1/2 truncate rounded-full bg-brand px-2 py-[3px] text-[8.5px] font-bold uppercase leading-none tracking-[0.08em] text-white">
                          Best daily value
                        </span>
                      )}
                      <span className="flex items-start justify-between gap-1">
                        <span className="font-display text-[15px] font-bold leading-tight text-brand-deep">
                          {daysForQty(opt.qty)} days
                        </span>
                        <span
                          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                            isActive ? "border-brand" : "border-border"
                          }`}
                        >
                          {isActive && <span className="h-2 w-2 rounded-full bg-brand" />}
                        </span>
                      </span>
                      <span className="mt-1 text-[11px] leading-tight text-muted-foreground">
                        {opt.unitsLabel}
                      </span>
                      <span className="mt-2 font-display text-[19px] font-bold leading-none tabular-nums text-brand-deep">
                        {formatInr(opt.price)}
                      </span>
                      {opt.compareAt > opt.price && (
                        <span className="mt-1 text-[10.5px] leading-none text-muted-foreground/80 line-through">
                          {formatInr(opt.compareAt)}
                        </span>
                      )}
                      <span className="mt-1.5 text-[11px] leading-none tabular-nums text-muted-foreground">
                        ₹{perDay(opt).toFixed(2)}/day
                      </span>
                      {opt.price >= NO_COST_EMI_MIN_INR && (
                        <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-brand-soft/70 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-brand-deep ring-1 ring-brand/20">
                          <CreditCard className="h-2.5 w-2.5" /> {t("pack.noCostEmi")}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {savingVsSingles > 0 && single && (
                <p className="mt-3 flex items-center gap-2 rounded-xl bg-brand-soft/50 px-3.5 py-2.5 text-[12.5px] font-semibold text-brand">
                  <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  Save {formatInr(savingVsSingles)} vs {selected!.qty} separate 30-day packs.
                </p>
              )}
            </>
          );
        })()
      ) : (
        <div
          className="mt-3 grid grid-cols-3 gap-2"
          role="radiogroup"
          aria-label="Choose your pack size"
        >
          {PACK_OPTIONS.map((opt) => {
            const isActive = qty === opt.qty;
            const mrp = price * opt.qty;
            const final = mrp * (1 - opt.discount);
            const saved = mrp - final;
            return (
              <button
                key={opt.qty}
                type="button"
                role="radio"
                aria-checked={isActive}
                onClick={() => onSelect(opt.qty)}
                className={`group relative flex flex-col items-center rounded-2xl border px-2 pb-3.5 pt-5 text-center transition ${
                  isActive
                    ? "border-brand bg-brand-soft/60 shadow-[0_8px_30px_-12px_color-mix(in_oklab,var(--brand)_45%,transparent)]"
                    : "border-border bg-card hover:border-brand/40 hover:bg-brand-soft/30"
                }`}
              >
                <span
                  className={`absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${
                    isActive ? "bg-brand text-white" : "bg-brand-deep text-white"
                  }`}
                >
                  {opt.badge}
                </span>
                {opt.saveLabel && saved > 0 && (
                  <span className="mb-1.5 text-[11px] font-semibold text-brand-deep/70">
                    {t("pack.save", { amount: Math.round(saved) })}
                  </span>
                )}
                <p className="font-display text-lg font-bold tabular-nums text-brand-deep md:text-xl">
                  ₹{Math.round(final)}
                </p>
                {opt.discount > 0 && (
                  <p className="text-[11.5px] text-muted-foreground line-through">
                    ₹{Math.round(mrp)}
                  </p>
                )}
                <p className="mt-1.5 text-[12px] font-semibold leading-snug text-brand-deep">
                  {opt.supply}
                </p>
                <p className="text-[11px] leading-snug text-muted-foreground">
                  {opt.cadence}
                </p>
                <p className="mt-1 text-[12.5px] font-bold leading-tight text-brand tabular-nums">
                  {t("pack.perDay", { amount: Math.round(final / daysForQty(opt.qty)) })}
                </p>
              </button>
            );
          })}
        </div>
      )}

      {whyOpen && (
        <div className="mt-3 animate-fade-in rounded-xl border border-brand/20 bg-brand-soft/40 p-3.5 text-[13.5px] leading-relaxed text-brand-deep">
          <p className="font-semibold uppercase tracking-wider text-brand text-[11.5px]">
            {t("pack.whatYouPayForTitle")}
          </p>
          <p className="mt-1.5">
            {t("pack.whatYouPayForBody1")}
          </p>
          <p className="mt-2">
            {t("pack.whatYouPayForBody2")}
          </p>
          <p className="mt-2 font-semibold">
            {t("pack.whatYouPayForBody3")}
          </p>
        </div>
      )}
    </div>
  );
}
